import { useTranslation } from 'react-i18next';

import { useUser } from '@/features/auth/hooks/use-user';
import {
  ClientProjectDeletedNotificationMetadata,
  ContractorProjectDeletedNotificationMetadata,
  KpiBreakpointNotificationMetadata,
  KpiBudgetExceededNotificationMetadata,
  TrackingOverdueNotificationMetadata,
  Notification,
  NotificationContentType,
  OrgMemberNotificationMetadata,
  ProjectDeadlineExceededNotificationMetadata,
  ProjectEndHintNotificationMetadata,
  ProjectMemberNotificationMetadata,
  TrackingRequestReceivedNotificationMetadata,
  TrackingRequestSentNotificationMetadata,
  ContractorInvitationAcceptedNotificationMetadata,
  ContractorInvitationNotificationMetadata,
} from '@/features/notifications/notification-types';

export const NotificationContentDescription = ({
  notification,
}: {
  notification: Notification;
}) => {
  const { t: sharedT } = useTranslation('shared');
  const { t } = useTranslation('notification', {
    keyPrefix: 'ContentTypesDescriptions',
  });
  const { t: rolesT } = useTranslation('org', { keyPrefix: 'Roles' });
  const { data: currentUser } = useUser();

  switch (notification.content_type) {
    case NotificationContentType.new_org_member: {
      const orgMemberMetadata =
        notification.metadata as OrgMemberNotificationMetadata;
      return (
        <div>
          {t('new_org_member', {
            user_name: orgMemberMetadata.user_name,
            role: rolesT(orgMemberMetadata.role as any),
          })}
        </div>
      );
    }
    case NotificationContentType.new_project_member: {
      const projectMemberMetadata =
        notification.metadata as ProjectMemberNotificationMetadata;
      const isSelf = currentUser?.id === notification.user_id;

      return (
        <div>
          {isSelf
            ? t('new_project_member_self', {
                project_name: projectMemberMetadata.project_name,
              })
            : t('new_project_member', {
                user_name: projectMemberMetadata.user_name,
                project_name: projectMemberMetadata.project_name,
              })}
        </div>
      );
    }
    case NotificationContentType.project_end_hint: {
      const projectEndHintMetadata =
        notification.metadata as ProjectEndHintNotificationMetadata;
      return (
        <div>
          {t('project_end_hint', {
            project_name: projectEndHintMetadata.project_name,
          })}
        </div>
      );
    }
    case NotificationContentType.project_deadline_exceeded: {
      const projectDeadlineExceededMetadata =
        notification.metadata as ProjectDeadlineExceededNotificationMetadata;
      return (
        <div>
          {t('project_deadline_exceeded', {
            project_name: projectDeadlineExceededMetadata.project_name,
          })}
        </div>
      );
    }
    case NotificationContentType.tracking_request_sent: {
      const trackingRequestSentMetadata =
        notification.metadata as TrackingRequestSentNotificationMetadata;
      return (
        <div>
          {t('tracking_request_sent', {
            contractor_project_name:
              trackingRequestSentMetadata.contractor_project_name,
          })}
        </div>
      );
    }
    case NotificationContentType.tracking_request_received: {
      const trackingRequestReceivedMetadata =
        notification.metadata as TrackingRequestReceivedNotificationMetadata;
      return (
        <div>
          {t('tracking_request_received', {
            client_org_name: trackingRequestReceivedMetadata.client_org_name,
            contractor_project_name:
              trackingRequestReceivedMetadata.contractor_project_name,
          })}
        </div>
      );
    }
    case NotificationContentType.kpi_breakpoint: {
      const kpiBreakpointMetadata =
        notification.metadata as KpiBreakpointNotificationMetadata;
      return (
        <div>
          {t('kpi_breakpoint', {
            kpi_name: kpiBreakpointMetadata.kpi_name,
            project_name: kpiBreakpointMetadata.project_name,
            percentage: kpiBreakpointMetadata.current_percentage,
          })}
        </div>
      );
    }
    case NotificationContentType.kpi_budget_exceeded: {
      const kpiBudgetExceededMetadata =
        notification.metadata as KpiBudgetExceededNotificationMetadata;
      return (
        <div>
          {t('kpi_budget_exceeded', {
            kpi_name: kpiBudgetExceededMetadata.kpi_name,
            project_name: kpiBudgetExceededMetadata.project_name,
          })}
        </div>
      );
    }
    case NotificationContentType.tracking_overdue: {
      const trackingOverdueMetadata =
        notification.metadata as TrackingOverdueNotificationMetadata;
      return (
        <div>
          {t('tracking_overdue', {
            project_name: trackingOverdueMetadata.project_name,
          })}
        </div>
      );
    }
    case NotificationContentType.contractor_invitation_accepted: {
      const contractorInvitationAcceptedMetadata =
        notification.metadata as ContractorInvitationAcceptedNotificationMetadata;
      return (
        <div>
          {t('contractor_invitation_accepted', {
            contractor_org_name:
              contractorInvitationAcceptedMetadata.contractor_org_name,
            project_name: contractorInvitationAcceptedMetadata.project_name,
          })}
        </div>
      );
    }
    case NotificationContentType.contractor_invitation: {
      const contractorInvitationMetadata =
        notification.metadata as ContractorInvitationNotificationMetadata;
      return (
        <div>
          {t('contractor_invitation', {
            inviter_org_name: contractorInvitationMetadata.inviter_org_name,
            project_name: contractorInvitationMetadata.project_name,
          })}
        </div>
      );
    }
    case NotificationContentType.client_project_deleted: {
      const clientProjectDeletedMetadata =
        notification.metadata as ClientProjectDeletedNotificationMetadata;
      return (
        <div>
          {t('client_project_deleted', {
            client_org_name:
              clientProjectDeletedMetadata.client_org_name ??
              sharedT('unknown_value'),
            contractor_project_name:
              clientProjectDeletedMetadata.contractor_project_name,
          })}
        </div>
      );
    }
    case NotificationContentType.contractor_project_deleted: {
      const contractorProjectDeletedMetadata =
        notification.metadata as ContractorProjectDeletedNotificationMetadata;
      return (
        <div>
          {t('contractor_project_deleted', {
            contractor_org_name:
              contractorProjectDeletedMetadata.contractor_org_name ??
              sharedT('unknown_value'),
            client_project_name:
              contractorProjectDeletedMetadata.client_project_name,
          })}
        </div>
      );
    }
    default:
      return <div>Unknown notification type</div>;
  }
};
